<script>
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import SimpleGroupSelect from '../UI/Form/SimpleGroupSelect.vue'
import EquipmentFormAndModal from './EquipmentFormAndModal.vue'

export default {
  components: { EquipmentFormAndModal, SimpleGroupSelect, ButtonAll, ModalConfirmation },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    disabled: { type: Boolean, default: false },
    mainEquipment: { required: false, default: null },
    equipments: { type: Array, required: false, default: [] },
    simulationId: { type: Number, required: false, default: null },
    platformParamId: { type: Number, required: false },
    platformId: { type: Number, required: false },
    maxRent: { type: [Number, String], default: 0 },
    total: { type: [Number, String], default: 0 },
    outstanding: { type: [Number, String], default: 0 },
    suppliers: { type: Object, default: {} },
    context: { type: String },
  },

  data() {
    return {
      checkedEquipments: [],
    }
  },
  computed: {
    sortedEquipments() {
      return [this.mainEquipment, ...this.equipments]
    },
    hasUnitAmountSoft() {
      if (this.mainEquipment?.unitAmountSoft) {
        return true
      }
      for (const equipment of this.equipments) {
        if (equipment.unitAmountSoft) {
          return true
        }
      }
      return false
    },
    hasUnitAmountPrestation() {
      if (this.mainEquipment?.unitAmountPrestation) {
        return true
      }
      for (const equipment of this.equipments) {
        if (equipment.unitAmountPrestation) {
          return true
        }
      }
      return false
    },
    uniqueSupplier() {
      if (this.equipments.length === 0) {
        return true
      }
      const firstSupplierId = this.mainEquipment?.supplier?.id
      return this.suppliersChoices.every((supplier) => supplier.id === firstSupplierId)
    },
    equipmentsAmount() {
      const outstanding = Number(this.outstanding || 0)
      return Number(this.total) + outstanding
    },
    equipmentsAmountFixed() {
      return this.equipmentsAmount.toFixed(2)
    },
    isEquipmentExceedingAmount() {
      return (
        this.maxRent > 0 &&
        this.truncateToTwoDecimals(this.totalAmount) > this.truncateToTwoDecimals(this.equipmentsAmount)
      )
    },
    suppliersChoices() {
      let suppliers = []
      this.sortedEquipments.forEach((equipment) => {
        if (equipment.supplier) {
          suppliers.push(equipment.supplier)
        }
      })

      return suppliers
    },
    totalUnitAmount() {
      return this.getTotal('unitAmount')
    },
    shareUnitAmount() {
      return this.getShare(this.totalUnitAmount)
    },
    totalSoftAmount() {
      return this.getTotal('unitAmountSoft')
    },
    shareSoftAmount() {
      return this.getShare(this.totalSoftAmount)
    },
    totalPrestationAmount() {
      return this.getTotal('unitAmountPrestation')
    },
    sharePrestationAmount() {
      return this.getShare(this.totalPrestationAmount)
    },
    totalAmount() {
      return this.sortedEquipments.reduce((total, equipment) => total + Number(equipment.amount ?? 0), 0)
    },
  },

  methods: {
    truncateToTwoDecimals(num) {
      return Number(num.toFixed(2))
    },
    sendReset() {
      window.location = 'simulation.php?i=' + this.simulationId + '&reset=true&index=creer'
    },
    equipmentSent(e) {
      this.$emit('equipment-sent', e)
    },
    getTotal(type) {
      return this.sortedEquipments.reduce(
        (total, equipment) => total + Number(equipment[type] ?? 0) * (equipment.quantity ?? 1),
        0,
      )
    },
    getShare(value) {
      if (this.totalAmount === 0 || value === 0) {
        return 0
      }

      return value / this.totalAmount
    },
    unitAmountCalculate(equipments, type) {
      let allAmounts = []
      for (const [index, equipment] of Object.entries(equipments)) {
        allAmounts.push(equipment[type] * equipment['quantity'])
      }
      const sum = allAmounts.reduce((partialSum, a) => partialSum + a, 0)

      return Math.round(sum * 100) / 100
    },
    unitPercentCalculate(equipments, type) {
      const totalAmountUnit = this.unitAmountCalculate(equipments, 'unitAmount')
      const totalAmountSoft = this.unitAmountCalculate(equipments, 'unitAmountSoft')
      const totalAmountPrestation = this.unitAmountCalculate(equipments, 'unitAmountPrestation')

      let percent = 0

      if (type == 'unitAmount') {
        percent = Math.round(((totalAmountUnit + Number.EPSILON) * 100) / this.totalAmount)
        if (percent == 'Infinity') {
          return 0
        }
        return percent
      } else if (type == 'unitAmountSoft') {
        percent = Math.round(((totalAmountSoft + Number.EPSILON) * 100) / this.totalAmount)
        if (percent == 'Infinity') {
          return 0
        }
        return percent
      } else {
        percent = Math.round(((totalAmountPrestation + Number.EPSILON) * 100) / this.totalAmount)
        if (percent == 'Infinity') {
          return 0
        }
        return percent
      }
    },
    checkAll() {
      if (this.checkedEquipments.length === 0) {
        this.checkedEquipments = this.equipments.map((equipment) => {
          return equipment.id
        })
      } else {
        this.checkedEquipments = []
      }
    },
    deleteEquipment() {
      let url = 'equipments/' + this.mainEquipment.id
      this.deleteJSON(url, { equipmentsId: this.checkedEquipments }, {}).then((json) => {
        if (json === 'alert') {
          alert('Un équipement est déjà associé à un PV de livraison')
        } else {
          this.checkedEquipments = this.checkedEquipments.filter((id) => !json.includes(id))
          document.getElementById('checks').checked = false
          this.$refs.modalconfirmation.closeModal()
          this.$emit('equipment-deleted')
        }
      })
    },
    showEquipmentDeleteModal() {
      this.$refs['modalconfirmation'].showModal()
    },
  },
}
</script>
<template>
  <div id="equipment-list">
    <ModalConfirmation message="equipment.delete.message" ref="modalconfirmation" @confirm="deleteEquipment" />
    <div class="table-responsive-sm">
      <div class="text-right" v-if="!user.isReadOnly && sortedEquipments.length > 1">
        <label for="checks" class="fs-16 font-weight-bold">{{ $t('delivery.checkAll') }}</label>
        <input type="checkbox" id="checks" @change="checkAll()" />
      </div>
      <div v-if="checkedEquipments.length > 0" class="text-center">
        <ButtonAll
          icon="trash"
          type="danger"
          label="equipment.delete.title"
          message="equipment.delete.message"
          e2e-button="deleteAllEquipments"
          @click="showEquipmentDeleteModal"
        />
      </div>

      <form enctype="multipart/form-data" style="overflow-x: auto" novalidate>
        <table class="table table-striped table-hover fs-16">
          <thead>
            <tr>
              <th>{{ $t('equipment.fields.id.label') }}</th>
              <th style="padding-bottom: 0 !important" v-if="!uniqueSupplier">
                <SimpleGroupSelect
                  id="supplierList"
                  name="supplierList"
                  :pre-select-value="false"
                  placeholder-t="equipment.fields.supplier.placeholder"
                  :config-options="suppliersChoices"
                  :disabled="suppliers.options.disabled"
                  @transmitted="supplierChange"
                />
              </th>
              <th v-else>{{ $t('main.supplier') }}</th>
              <th v-if="isTheme('eurolocatique')">{{ $t('equipment.fields.equipmentType.label') }}</th>
              <th v-if="isTheme('eurolocatique')">{{ $t('equipment.fields.productType.label') }}</th>
              <th>{{ $t('equipment.fields.brand.label') }}</th>
              <th>{{ $t('equipment.fields.model.label') }}</th>
              <th>{{ $t('equipment.fields.serialNumber.label') }}</th>
              <th class="text-right">{{ $t('equipment.fields.unitAmount.label') }}</th>
              <th class="text-right" v-if="hasUnitAmountSoft">{{ $t('equipment.fields.unitAmountSoft.label') }}</th>
              <th class="text-right" v-if="hasUnitAmountPrestation">
                {{ $t('equipment.fields.unitAmountPrestation.label') }}
              </th>
              <th class="text-right">{{ $t('equipment.fields.quantity.label') }}</th>
              <th class="text-right">{{ $t('equipment.fields.amount.label') }}</th>
              <th class="text-right" v-if="isTheme('ciscar')">
                {{ $t('equipment.fields.unitAmountPurchaseSent.label') }}
              </th>
              <th class="text-right" v-if="hasRole('ROLE_STATUT_EQUIPMENT_SHOW') && context !== 'simulation'">
                {{ $t('equipment.fields.status.label') }}
              </th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="equipmentItem in sortedEquipments" :e2e-row="'equipment-' + equipmentItem.id">
              <td e2e-data="equipmentId">{{ equipmentItem.id }}</td>
              <td e2e-data="equipmentSupplierName">{{ equipmentItem.supplier ? equipmentItem.supplier.name : '' }}</td>
              <td v-if="isTheme('eurolocatique')" e2e-data="equipment">
                {{ equipmentItem.equipmentType ? equipmentItem.equipmentType.name : '' }}
              </td>
              <td v-if="isTheme('eurolocatique')" e2e-data="equipmentProductTypeName">
                {{ equipmentItem.productType ? equipmentItem.productType.name : '' }}
              </td>
              <td e2e-data="equipmentBrand">{{ equipmentItem.brand }}</td>
              <td e2e-data="equipmentModel">{{ equipmentItem.model }}</td>
              <td e2e-data="equipmentSerialNumber">{{ equipmentItem.serialNumber }}</td>
              <td class="text-right" e2e-data="equipmentUnitAmount">
                {{ $n(Number(equipmentItem.unitAmount), 'currency') }}
              </td>
              <td class="text-right" v-if="hasUnitAmountSoft" e2e-data="equipmentUnitAmountSoft">
                {{ $n(Number(equipmentItem.unitAmountSoft), 'currency') }}
              </td>
              <td class="text-right" v-if="hasUnitAmountPrestation" e2e-data="equipmentUnitAmountPrestation">
                {{ $n(Number(equipmentItem.unitAmountPrestation), 'currency') }}
              </td>
              <td class="text-right" e2e-data="equipmentQuantity">
                {{ equipmentItem.quantity }}
              </td>
              <td class="text-right" e2e-data="equipmentAmount">{{ $n(Number(equipmentItem.amount), 'currency') }}</td>
              <td class="text-right" v-if="isTheme('ciscar')" e2e-data="equipmentRealUnitAmountPurchase">
                {{ $n(Number(equipmentItem.realUnitAmountPurchase), 'currency') }}
              </td>
              <td
                class="text-right"
                v-if="hasRole('ROLE_STATUT_EQUIPMENT_SHOW') && context !== 'simulation'"
                e2e-data="equipmentStatus"
              >
                {{ $t('equipment.status.' + equipmentItem.status + '.label') }}
              </td>
              <td v-if="!user.isReadOnly" class="d-flex" e2e-data="equipmentAction">
                <EquipmentFormAndModal
                  :ref="equipmentItem.id"
                  :equipment="equipmentItem"
                  :main-id="equipmentItem.mainId"
                  :platform-param-id="platformParamId"
                  :platform-id="platformId"
                  :context="context"
                  :disabled="disabled"
                  @equipment-sent="equipmentSent"
                />
                <input
                  v-if="equipmentItem.id != equipmentItem.mainId"
                  type="checkbox"
                  class="ml-3"
                  ref="inputCheckbox"
                  :id="equipmentItem.id"
                  v-model="checkedEquipments"
                  :value="equipmentItem.id"
                  e2e-input="equipmentChecked"
                />
              </td>
            </tr>
          </tbody>
        </table>

        <EquipmentFormAndModal
          v-if="!user.isReadOnly && hasRole('ROLE_ADD_EQUIPMENT_SHOW')"
          add-equipment
          class="mr-3"
          :main-id="mainEquipment.id"
          :platform-param-id="platformParamId"
          :platform-id="platformId"
          :context="context"
          :disabled="disabled"
          @equipment-sent="equipmentSent"
        />
      </form>

      <div v-if="isEquipmentExceedingAmount" class="row">
        <div class="my-4 py-3 col-11 mx-auto" style="border-radius: 1em; border: 3px solid var(--danger)">
          <div class="text-center text-black">
            {{
              $t('equipment.exceeding', {
                computed: $n(Number(totalAmount), 'currency'),
                initial: $n(Number(equipmentsAmountFixed), 'currency'),
              })
            }}
          </div>
          <div v-if="simulationId" class="row d-flex justify-content-center" style="margin: 1em auto">
            <button
              class="btn btn-primary mx-3"
              id="reset"
              style="white-space: unset"
              name="reset"
              @click="sendReset"
              e2e-button="redoSimulation"
            >
              {{ $t('simulation.input.button.computeReset') }}
            </button>
          </div>
        </div>
      </div>

      <table class="table fs-16">
        <thead>
          <tr>
            <th class="text-center">{{ $t('equipment.totalAmountUnit') }}</th>
            <th class="text-center" v-if="hasUnitAmountSoft">{{ $t('equipment.totalAmountSoft') }}</th>
            <th class="text-center" v-if="hasUnitAmountPrestation">{{ $t('equipment.totalAmountPrestation') }}</th>
            <th class="text-center">{{ $t('equipment.total') }}</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td class="text-center font-weight-bold" e2e-data="equipmentUnitAmount">
              {{
                $t('equipment.totalPercent', {
                  amount: $n(totalUnitAmount, 'currency'),
                  percent: $n(shareUnitAmount, 'percent'),
                })
              }}
            </td>
            <td v-if="totalSoftAmount > 0" class="text-center font-weight-bold" e2e-data="equipmentUnitAmountSoft">
              {{
                $t('equipment.totalPercent', {
                  amount: $n(totalSoftAmount, 'currency'),
                  percent: $n(shareSoftAmount, 'percent'),
                })
              }}
            </td>
            <td
              v-if="totalPrestationAmount > 0"
              class="text-center font-weight-bold"
              e2e-data="equipmentUnitAmountPrestation"
            >
              {{
                $t('equipment.totalPercent', {
                  amount: $n(totalPrestationAmount, 'currency'),
                  percent: $n(sharePrestationAmount, 'percent'),
                })
              }}
            </td>
            <td class="text-center font-weight-bold" e2e-data="equipmentTotal">
              {{ $t('equipment.totalPercent', { amount: $n(totalAmount, 'currency'), percent: $n(1, 'percent') }) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
